import React, { useEffect, useMemo, useState } from "react";
import "./CalendarioLateralAluno.scss";
import { useNavigate } from "react-router-dom";
import { getUsuarioLogadoAdaptado as getUsuarioLogado } from "../../utils/ids";
import { ocorrenciasCalendario } from "../../utils/eventosCalendario";
import { recarregarSeNecessario } from "../../services/eventosStore";

function parseDateOnly(value) {
  if (!value) return null;
  const raw = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) {
    const [year, month, day] = raw.slice(0, 10).split("-").map(Number);
    return new Date(year, month - 1, day);
  }
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function startOfToday() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate());
}

function toDateKey(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function formatGroupLabel(date, today) {
  if (!date) return "";
  if (date.getTime() === today.getTime()) return "Hoje";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}`;
}

function clampDescription(text, max = 58) {
  const value = String(text || "").trim();
  if (value.length <= max) return value;
  return `${value.slice(0, max).trim()}...`;
}

function lerMostrarSecundarias() {
  return localStorage.getItem("menuAlunoSecundariasVisiveis") === "true";
}

export default function CalendarioAlunoLateral() {
  const [dataVersion, setDataVersion] = useState(0);
  const [openKeys, setOpenKeys] = useState([]);
  const [mostrarSecundarias, setMostrarSecundarias] = useState(
    lerMostrarSecundarias,
  );

  const navigate = useNavigate();
  const today = useMemo(() => startOfToday(), []);

  useEffect(() => {
    recarregarSeNecessario().catch(() => {});

    const bump = () => setDataVersion((v) => v + 1);
    const syncSecundarias = () => {
      setMostrarSecundarias(lerMostrarSecundarias());
      bump();
    };

    window.addEventListener("app:data-changed", bump);
    window.addEventListener("eventos:changed", bump);
    window.addEventListener("disciplinaAtual:changed", bump);
    window.addEventListener("menuAlunoSecundarias:changed", syncSecundarias);

    return () => {
      window.removeEventListener("app:data-changed", bump);
      window.removeEventListener("eventos:changed", bump);
      window.removeEventListener("disciplinaAtual:changed", bump);
      window.removeEventListener(
        "menuAlunoSecundarias:changed",
        syncSecundarias,
      );
    };
  }, []);

  const usuarioLogado = useMemo(() => {
    if (dataVersion < 0) return null;
    return getUsuarioLogado();
  }, [dataVersion]);
  const tipoUsuario = String(usuarioLogado?.tipo || "").toLowerCase();
  const isAluno = tipoUsuario === "aluno" || tipoUsuario === "responsavel";

  // Agrupa as ocorrências de calendário (evento×turma) por dia, só de hoje em
  // diante, ordenadas por data.
  const grupos = useMemo(() => {
    if (!isAluno) return [];

    const map = new Map();
    ocorrenciasCalendario(usuarioLogado, { mostrarSecundarias }).forEach(
      (oc) => {
        const data = parseDateOnly(oc.data);
        if (!data || data < today) return;
        const key = toDateKey(data);

        if (!map.has(key)) {
          map.set(key, {
            key,
            date: data,
            label: formatGroupLabel(data, today),
            eventos: [],
          });
        }

        map.get(key).eventos.push({
          ...oc,
          __uniqueKey: `${key}-${oc.eventoId}-${oc.turmaId}`,
        });
      },
    );

    return Array.from(map.values())
      .sort((a, b) => a.date - b.date)
      .map((g) => ({
        ...g,
        eventos: g.eventos.sort((a, b) => {
          const ua = new Date(a.ultimaAtualizacao || 0).getTime();
          const ub = new Date(b.ultimaAtualizacao || 0).getTime();
          return ub - ua;
        }),
      }));
    // usuarioLogado muda de referência a cada bump do store, disparando a
    // releitura de getEventos() aqui.
  }, [isAluno, usuarioLogado, today, mostrarSecundarias]);

  useEffect(() => {
    const keysValidas = new Set(grupos.map((grupo) => grupo.key));
    setOpenKeys((prev) => prev.filter((key) => keysValidas.has(key)));
  }, [grupos]);

  function toggleGroup(key) {
    setOpenKeys((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key],
    );
  }

  if (!isAluno) return null;

  return (
    <aside className="calendario-aluno-lateral">
      {grupos.length === 0 ? (
        <div className="calendario-aluno-lateral__empty">
          Nenhum evento de hoje ou futuro.
        </div>
      ) : (
        grupos.map((grupo) => {
          const isOpen = openKeys.includes(grupo.key);

          return (
            <section
              key={grupo.key}
              className="calendario-aluno-lateral__group"
            >
              <button
                type="button"
                className="calendario-aluno-lateral__groupHeader"
                onClick={() => toggleGroup(grupo.key)}
              >
                <span className="calendario-aluno-lateral__groupLabel">
                  {grupo.label}
                </span>

                <span
                  className={`calendario-aluno-lateral__chevron ${
                    isOpen ? "is-open" : ""
                  }`}
                >
                  <svg viewBox="0 0 12 12" aria-hidden="true">
                    <path
                      d="M3 4.5L6 7.5L9 4.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              </button>

              {isOpen && (
                <div className="calendario-aluno-lateral__list">
                  {grupo.eventos.map((evento, index) => (
                    <article
                      key={evento.__uniqueKey || `${grupo.key}-${index}`}
                      className={`calendario-aluno-lateral__card ${
                        evento.vistoCalendario ? "is-visto" : ""
                      }`}
                      style={{
                        "--bar-color": evento.cor,
                        "--dot-color": evento.cor,
                        cursor: "pointer",
                      }}
                      onClick={() => {
                        if (!evento.data) return;
                        navigate(`/detalhe-calendario-aluno/${evento.data}`);
                      }}
                    >
                      <h4 className="calendario-aluno-lateral__title">
                        {evento.titulo}
                      </h4>

                      <p className="calendario-aluno-lateral__description">
                        {clampDescription(evento.descricao, 62)}
                      </p>

                      <div className="calendario-aluno-lateral__tag">
                        <span className="calendario-aluno-lateral__dot" />
                        <span>{evento.turmaNome}</span>
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          );
        })
      )}
    </aside>
  );
}
